import { useEffect, useRef, useState, type PointerEvent } from 'react';

/** Height of the plotted area in px; date labels add their own below it. */
const PLOT_H = 180;
const MARGIN = { top: 16, right: 12 };
/** Room under the plot for level date labels. */
const LEVEL_BOTTOM = 28;
/** Rough px per character of the xs tick text — enough to size margins. */
const CHAR = 7;
/** Horizontal px between slanted labels: one line of xs text, plus air. */
const SLANT_GAP = 16;
const SIN45 = Math.SQRT1_2;
/** The tooltip's padding plus slack, and its rough height, for placing it. */
const TIP_PAD = 24;
const TIP_H = 44;
const TIP_GAP = 10;
const DAY_MS = 86_400_000;

/** Days since the epoch, in UTC so a DST switch cannot move a point. */
function dayIndex(date: string): number {
  return Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
}

/**
 * Round ticks from at or below `min` up to at or above `max`, in about four
 * 1/2/5 × 10ⁿ steps. Ticks are multiples of the step, so they read cleanly.
 */
function valueTicks(min: number, max: number): number[] {
  if (!(max > min)) return [min, min + 1];
  const raw = (max - min) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  const from = Math.floor(min / step);
  const to = Math.ceil(max / step);
  // toPrecision drops float dust like 80.49999999999999.
  return Array.from({ length: to - from + 1 }, (_, i) =>
    Number(((from + i) * step).toPrecision(12)),
  );
}

/**
 * The axis span for `fit`: the values and the reference, padded by a tenth of
 * their spread — at least half a unit, so a flat series still gets a band.
 */
function fittedRange(values: number[]): [number, number] {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = Math.max((hi - lo) / 10, 0.5);
  return [lo - pad, hi + pad];
}

/**
 * Indices of the points that get a date label: every `stride`-th, counted back
 * from the last day so the newest is always labelled and the gaps stay even.
 */
function dateTicks(count: number, stride: number): number[] {
  const ticks = [];
  for (let i = count - 1; i >= 0; i -= stride) ticks.push(i);
  return ticks;
}

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.floor(entry.contentRect.width)),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * One value per day, as a line. Pass every day of the range: a `null` value
 * breaks the line there instead of dropping to zero. The vertical axis starts
 * at 0 — or, with `axis="fit"`, hugs the values — and always reaches
 * `reference`, drawn as a dashed, labelled line. Formatting is the caller's,
 * in the screen's locale.
 */
export function LineChart({
  label,
  points,
  reference,
  axis = 'zero',
  formatDate,
  formatValue,
}: {
  /** What the chart shows, for assistive technology. */
  label: string;
  points: { date: string; value: number | null }[];
  reference?: { value: number; label: string };
  /** `fit` when the change matters more than the size, like body weight. */
  axis?: 'zero' | 'fit';
  formatDate: (date: string) => string;
  formatValue: (value: number) => string;
}) {
  const [ref, width] = useWidth();

  return (
    <div role="figure" aria-label={label} className="line-chart">
      <div ref={ref} className="line-chart-canvas">
        {width > 0 && points.length > 0 && (
          <Plot
            width={width}
            points={points}
            reference={reference}
            axis={axis}
            formatDate={formatDate}
            formatValue={formatValue}
          />
        )}
      </div>
      <dl className="line-chart-data">
        {points.map(({ date, value }) => (
          <div key={date}>
            <dt>{formatDate(date)}</dt>
            <dd>{value === null ? '—' : formatValue(value)}</dd>
          </div>
        ))}
        {reference && (
          <div>
            <dt>{reference.label}</dt>
            <dd>{formatValue(reference.value)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function Plot({
  width,
  points,
  reference,
  axis,
  formatDate,
  formatValue,
}: {
  width: number;
  points: { date: string; value: number | null }[];
  reference?: { value: number; label: string };
  axis: 'zero' | 'fit';
  formatDate: (date: string) => string;
  formatValue: (value: number) => string;
}) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);

  // A touch readout has no pointer to leave with; a tap elsewhere clears it.
  useEffect(() => {
    if (active === null) return;
    const clear = (e: globalThis.PointerEvent) => {
      if (!plotRef.current?.contains(e.target as Node)) setActive(null);
    };
    document.addEventListener('pointerdown', clear);
    return () => document.removeEventListener('pointerdown', clear);
  }, [active]);

  const values = points.flatMap((p) => (p.value === null ? [] : [p.value]));
  const spanned = reference ? [...values, reference.value] : values;
  const yTicks =
    axis === 'fit' && spanned.length > 0
      ? valueTicks(...fittedRange(spanned))
      : valueTicks(0, Math.max(0, ...spanned));
  const yBottom = yTicks[0];
  const yTop = yTicks[yTicks.length - 1];
  const left =
    Math.max(...yTicks.map((t) => formatValue(t).length)) * CHAR + 12;
  const plotW = Math.max(1, width - left - MARGIN.right);

  // Level labels when every day's fits under its point, centred, with the
  // days inset by half a label. Otherwise they slant at 45°, which needs far
  // less room per day; only if even that is too tight do days go unlabelled.
  const labelW =
    Math.max(...points.map((p) => formatDate(p.date).length)) * CHAR;
  const first = dayIndex(points[0].date);
  const span = dayIndex(points[points.length - 1].date) - first;
  const levelInset = Math.min(labelW / 2, plotW / 4);
  const slant = span > 0 && (plotW - 2 * levelInset) / span < labelW + 8;
  const inset = slant ? 6 : levelInset;
  const stride = slant
    ? Math.ceil(SLANT_GAP / ((plotW - 2 * inset) / span))
    : 1;
  const bottom = slant ? 12 + labelW * SIN45 + 8 : LEVEL_BOTTOM;
  const height = MARGIN.top + PLOT_H + bottom;

  const x = (date: string) =>
    span === 0
      ? left + plotW / 2
      : left + inset + ((dayIndex(date) - first) / span) * (plotW - 2 * inset);
  const y = (value: number) =>
    MARGIN.top + PLOT_H - ((value - yBottom) / (yTop - yBottom)) * PLOT_H;

  let path = '';
  let drawing = false;
  for (const { date, value } of points) {
    if (value === null) {
      drawing = false;
      continue;
    }
    path += `${drawing ? 'L' : 'M'}${x(date)},${y(value)}`;
    drawing = true;
  }

  const labelY = MARGIN.top + PLOT_H + (slant ? 12 : LEVEL_BOTTOM - 8);

  /** Snap to the day nearest the pointer — no need to hit its dot. */
  const pick = (e: PointerEvent) => {
    const px = e.clientX - e.currentTarget.getBoundingClientRect().left;
    let nearest = 0;
    points.forEach((p, i) => {
      if (Math.abs(x(p.date) - px) < Math.abs(x(points[nearest].date) - px))
        nearest = i;
    });
    setActive(nearest);
  };

  const hovered = active === null ? null : points[active];
  let tip = null;
  if (hovered) {
    const date = formatDate(hovered.date);
    const value = hovered.value === null ? '—' : formatValue(hovered.value);
    const tipW = Math.max(date.length, value.length) * CHAR + TIP_PAD;
    const at = x(hovered.date);
    const top = hovered.value === null ? MARGIN.top : y(hovered.value);
    const below = top - TIP_GAP - TIP_H < 0 || hovered.value === null;
    tip = (
      <div
        aria-hidden="true"
        className={below ? 'line-chart-tooltip is-below' : 'line-chart-tooltip'}
        style={{
          left: Math.min(Math.max(at, tipW / 2), width - tipW / 2),
          top: below ? top + TIP_GAP : top - TIP_GAP,
        }}
      >
        <span className="line-chart-tooltip-date">{date}</span>
        <span className="line-chart-tooltip-value">{value}</span>
      </div>
    );
  }

  return (
    <div
      ref={plotRef}
      className="line-chart-plot"
      onPointerMove={pick}
      onPointerDown={pick}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setActive(null);
      }}
    >
      <svg
        width={width}
        height={height}
        aria-hidden="true"
        className="line-chart-svg"
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={left}
              x2={left + plotW}
              y1={y(tick)}
              y2={y(tick)}
              className="line-chart-grid"
            />
            <text
              x={left - 8}
              y={y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              className="line-chart-tick"
            >
              {formatValue(tick)}
            </text>
          </g>
        ))}
        {dateTicks(points.length, stride).map((i) => {
          const at = x(points[i].date);
          return (
            <text
              key={points[i].date}
              x={at}
              y={labelY}
              textAnchor={slant ? 'end' : 'middle'}
              transform={slant ? `rotate(-45 ${at} ${labelY})` : undefined}
              className="line-chart-tick"
            >
              {formatDate(points[i].date)}
            </text>
          );
        })}
        {reference && (
          <g>
            <line
              x1={left}
              x2={left + plotW}
              y1={y(reference.value)}
              y2={y(reference.value)}
              className="line-chart-reference"
            />
            <text
              x={left + plotW}
              y={y(reference.value) - 6}
              textAnchor="end"
              className="line-chart-reference-label"
            >
              {reference.label}
            </text>
          </g>
        )}
        <path d={path} className="line-chart-series" />
        {points.map(
          ({ date, value }) =>
            value !== null && (
              <circle
                key={date}
                cx={x(date)}
                cy={y(value)}
                r={3}
                className="line-chart-dot"
              />
            ),
        )}
        {hovered && (
          <g>
            <line
              x1={x(hovered.date)}
              x2={x(hovered.date)}
              y1={MARGIN.top}
              y2={MARGIN.top + PLOT_H}
              className="line-chart-guide"
            />
            {hovered.value !== null && (
              <circle
                cx={x(hovered.date)}
                cy={y(hovered.value)}
                r={5}
                className="line-chart-dot-active"
              />
            )}
          </g>
        )}
      </svg>
      {tip}
    </div>
  );
}
